import OCGatewayClient from "../client/OCGatewayClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DeviceTransitPolicy } from "../models/DeviceTransitPolicy";
import { Device } from "../models/Device";

export function useDeviceTransitPolicyQuery(deviceTransitPolicyId?: number | null) {
    const query = useQuery({
        queryKey: ['getDeviceTransitPolicy', deviceTransitPolicyId],
        queryFn: async () => {
            return OCGatewayClient.request('getDeviceTransitPolicy', {
                deviceTransitPolicyId
            }) as Promise<DeviceTransitPolicy>;
        },
        enabled: deviceTransitPolicyId != null,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 60 * 24,
        networkMode: 'always',
        retry: false
    });

    return query;
}

export function useDeviceTransitPolicyActivateMutation(deviceId: string) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (deviceTransitPolicyId: number) => {
            await OCGatewayClient.request('activateDeviceTransitPolicy', {
                deviceId,
                deviceTransitPolicyId
            });
            return deviceTransitPolicyId;
        },
        onMutate: async (deviceTransitPolicyId: number) => {
            await queryClient.cancelQueries({ queryKey: ['getDevice', deviceId] });

            const previousDevice = queryClient.getQueryData(['getDevice', deviceId]) as Device;

            queryClient.setQueryData(['getDevice', deviceId], (queryData: any) => {
                return Object.assign({}, queryData, { deviceTransitPolicyId });
            });

            return { previousDevice };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['getDevice', deviceId], (queryData: any) => {
                return Object.assign({}, queryData, { deviceTransitPolicyId: context?.previousDevice?.deviceTransitPolicyId });
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['getDevice', deviceId] });
            // Refresh activatedAt so recency ordering reflects this activation.
            queryClient.invalidateQueries({ queryKey: ['getDeviceTransitPolicies', deviceId] });
        }
    });

    return mutation;
};

export function useDeviceTransitPolicyMutation(deviceTransitPolicyId: number) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (deviceTransitPolicy: Partial<DeviceTransitPolicy>) => {
            return OCGatewayClient.request('updateDeviceTransitPolicy', {
                deviceTransitPolicyId,
                ...deviceTransitPolicy
            });
        },
        onMutate: async (deviceTransitPolicy: Partial<DeviceTransitPolicy>) => {
            await queryClient.cancelQueries({ queryKey: ['getDeviceTransitPolicy', deviceTransitPolicyId] });

            const previousDeviceTransitPolicy = queryClient.getQueryData(['getDeviceTransitPolicy', deviceTransitPolicyId]) as DeviceTransitPolicy;

            queryClient.setQueryData(['getDeviceTransitPolicy', deviceTransitPolicyId], (queryData: any) => {
                return Object.assign({}, queryData, deviceTransitPolicy);
            });

            return { previousDeviceTransitPolicy };
        },
        onError: (error, variables, context) => {
            if (context?.previousDeviceTransitPolicy) {
                queryClient.setQueryData(['getDeviceTransitPolicy', deviceTransitPolicyId], context.previousDeviceTransitPolicy);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['getDeviceTransitPolicy', deviceTransitPolicyId] });
        }
    });

    return mutation;
}
